import {
  normalizeDeliverySelection,
  resolveNotificationDeliverySelection,
} from '../../utils/notification-delivery-selection.util';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { PiBellLight, PiPlus, PiTrash } from 'react-icons/pi';
import { useAlert } from 'src/contexts/alert-context';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { Text } from 'src/modules/shared/components/atoms/text';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import Loading from 'src/modules/shared/components/atoms/loading';
import LabelSwitch from 'src/modules/shared/components/atoms/inputs/label-switch';
import LabelSelect from 'src/modules/shared/components/atoms/inputs/label-select';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import MultiSelect from 'src/modules/shared/components/atoms/inputs/multi-select';
import SearchableSelect, {
  type SearchableSelectOption,
} from 'src/modules/shared/components/atoms/inputs/searchable-select';
import Label from 'src/modules/shared/components/atoms/inputs/label';
import { Tabs } from 'src/modules/shared/components/atoms/tabs/tabs';
import { Tab } from 'src/modules/shared/components/atoms/tabs/tab';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import { ExpandableSection } from 'src/modules/shared/components/molecules/expandable-section';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import {
  useNotificationsPreferences,
  useNotificationsEvents,
  useNotificationsUpdatePreferences,
  useNotificationsEndpoints,
} from 'src/modules/notifications/hooks/api/notifications';
import {
  type NotificationChannelPreferences,
  type NotificationDnd,
  type NotificationDndSchedule,
  type NotificationDndChannel,
  type NotificationDndDay,
  type NotificationDeliveryChannel,
  type NotificationEventDefinition,
  type NotificationEventRule,
  type NotificationEventKey,
  type NotificationInAppPreferences,
  type NotificationPreferences,
  type NotificationPreferencesUpdateBodyParams,
  type NotificationChannel,
  type NotificationRoutingMode,
  type NotificationRoutingPreferences,
} from 'src/modules/notifications/types/notification.types';

const DEFAULT_CHANNELS: NotificationChannelPreferences = {
  messaging: true,
  marketing: true,
  calls: true,
};

const DEFAULT_ROUTING: NotificationRoutingPreferences = {
  mode: 'LAST_ACTIVE',
};

const DEFAULT_IN_APP: NotificationInAppPreferences = {
  showAlert: true,
  playSound: true,
};

const DEFAULT_DND: NotificationDnd = {
  enabled: false,
  behavior: {
    createInboxItem: true,
  },
};

const ALL_DND_DAYS: NotificationDndDay[] = [
  'MON',
  'TUE',
  'WED',
  'THU',
  'FRI',
  'SAT',
  'SUN',
];

const ALL_DND_CHANNELS: NotificationDndChannel[] = [
  'MESSAGING',
  'MARKETING',
  'CALLS',
];

const ALL_DELIVERY_CHANNELS: NotificationDeliveryChannel[] = [
  'IN_APP',
  'PUSH',
  'EMAIL',
  'SMS',
  'WHATSAPP',
];

const DEFAULT_DELIVERY_CHANNELS: NotificationDeliveryChannel[] = [
  'IN_APP',
  'PUSH',
];

const PHONE_DELIVERY_CHANNELS: NotificationDeliveryChannel[] = [
  'WHATSAPP',
  'SMS',
];

const createId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `id-${Math.random().toString(36).slice(2, 10)}`;
};

const createSchedule = (): NotificationDndSchedule => ({
  id: createId(),
  name: '',
  enabled: true,
  daysOfWeek: [...ALL_DND_DAYS],
  start: '09:00',
  end: '18:00',
  startDate: '',
  endDate: '',
  channels: [...ALL_DND_CHANNELS],
});

const normalizeEventRules = (
  rules?: NotificationEventRule[]
): NotificationEventRule[] => {
  if (!rules || rules.length === 0) {
    return [];
  }

  return [...rules]
    .filter((rule) => Boolean(rule.eventKey))
    .map((rule) => ({
      eventKey: rule.eventKey,
      enabled: rule.enabled,
      inApp: rule.inApp,
      routing: rule.routing,
      deliveryChannels: rule.deliveryChannels,
    }))
    .sort((a, b) => a.eventKey.localeCompare(b.eventKey));
};

const areDeliverySelectionsEqual = (
  a: NotificationDeliveryChannel[],
  b: NotificationDeliveryChannel[]
): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
};

const sanitizePhoneDeliveryChannels = (
  rules?: NotificationEventRule[]
): NotificationEventRule[] | undefined => {
  if (!rules || rules.length === 0) {
    return rules;
  }

  const sanitized = rules.map((rule) => {
    if (!rule.deliveryChannels) {
      return rule;
    }

    const filtered = rule.deliveryChannels.filter(
      (channel) => !PHONE_DELIVERY_CHANNELS.includes(channel)
    );

    if (filtered.length === rule.deliveryChannels.length) {
      return rule;
    }

    return {
      ...rule,
      deliveryChannels: filtered.length > 0 ? filtered : undefined,
    };
  });

  return sanitized.some((rule, index) => rule !== rules[index])
    ? sanitized
    : rules;
};

const isChannelEnabled = (
  channels: NotificationChannelPreferences,
  channel: NotificationChannel
): boolean => {
  if (channel === 'MESSAGING') {
    return channels.messaging;
  }

  if (channel === 'MARKETING') {
    return channels.marketing;
  }

  if (channel === 'CALLS') {
    return channels.calls;
  }

  return true;
};

const buildPreferences = (
  preferences?: NotificationPreferences
): NotificationPreferences => {
  const normalizedDnd = preferences?.dnd
    ? {
        ...preferences.dnd,
        snoozeUntil: undefined,
        behavior: {
          createInboxItem: true,
        },
        schedules: preferences.dnd.schedules?.map((schedule) => ({
          ...schedule,
          enabled: true,
          daysOfWeek: schedule.daysOfWeek,
          channels: [...ALL_DND_CHANNELS],
          behavior: undefined,
          priority: undefined,
          start: schedule.start,
          end: schedule.end,
          startDate: schedule.startDate ?? '',
          endDate: schedule.endDate ?? '',
        })),
        deviceOverrides: preferences.dnd.deviceOverrides?.map((override) => ({
          ...override,
          channels: override.channels ?? [...ALL_DND_CHANNELS],
        })),
      }
    : { ...DEFAULT_DND };

  return {
    tenantId: preferences?.tenantId ?? '',
    userId: preferences?.userId ?? '',
    channels: preferences?.channels ?? DEFAULT_CHANNELS,
    routing: {
      ...DEFAULT_ROUTING,
      ...preferences?.routing,
      primaryEndpointId: preferences?.routing.primaryEndpointId ?? undefined,
    },
    inApp: preferences?.inApp ?? DEFAULT_IN_APP,
    eventRules: normalizeEventRules(preferences?.eventRules),
    dnd: normalizedDnd,
    updatedAt: preferences?.updatedAt ?? new Date().toISOString(),
  };
};

const stripPreferences = (preferences: NotificationPreferences) => ({
  channels: preferences.channels,
  routing: preferences.routing,
  inApp: preferences.inApp,
  eventRules: normalizeEventRules(preferences.eventRules),
  dnd: preferences.dnd,
});

const SectionCard: React.FC<{
  title: string;
  description?: string;
  children: ReactNode;
}> = ({ title, description, children }) => (
  <div className="rounded-xl border border-secondary-200 bg-white p-4 shadow-sm">
    <div className="mb-4">
      <Text weight="medium" className="text-sm text-secondary-800">
        {title}
      </Text>
      {description && (
        <Text size="small" color="neutral" className="mt-1">
          {description}
        </Text>
      )}
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

export const NotificationsSettingsPage: React.FC = () => {
  const { t } = useI18n();
  const { showAlert } = useAlert();
  const { user: currentUser, loading: isCurrentUserLoading } = useCurrentUser();

  const {
    data: preferences,
    isLoading,
    error,
    refetch,
  } = useNotificationsPreferences();
  const {
    data: eventsResponse,
    isLoading: isEventsLoading,
    error: eventsError,
  } = useNotificationsEvents();
  const {
    data: endpointsResponse,
    isLoading: isEndpointsLoading,
    error: endpointsError,
  } = useNotificationsEndpoints();
  const updateMutation = useNotificationsUpdatePreferences();

  const [localPreferences, setLocalPreferences] =
    useState<NotificationPreferences | null>(null);
  const [showAppAdvanced, setShowAppAdvanced] = useState(false);
  const [showMarketingAdvanced, setShowMarketingAdvanced] = useState(false);
  const [showRoutingAdvanced, setShowRoutingAdvanced] = useState(false);

  const baselinePreferences = useMemo(
    () => (preferences ? buildPreferences(preferences) : null),
    [preferences]
  );

  const hasPhone = Boolean(currentUser?.individualPhoneNumber?.trim());
  const shouldValidatePhone = !isCurrentUserLoading;
  const restrictPhoneChannels = shouldValidatePhone && !hasPhone;

  useEffect(() => {
    if (baselinePreferences) {
      setLocalPreferences(baselinePreferences);
    }
  }, [baselinePreferences]);

  useEffect(() => {
    if (!localPreferences || !shouldValidatePhone || hasPhone) {
      return;
    }

    const sanitizedRules = sanitizePhoneDeliveryChannels(
      localPreferences.eventRules
    );

    if (sanitizedRules !== localPreferences.eventRules) {
      setLocalPreferences((current) =>
        current ? { ...current, eventRules: sanitizedRules } : current
      );
    }
  }, [hasPhone, localPreferences, shouldValidatePhone]);

  const routingOptions = useMemo(
    () => [
      {
        value: 'LAST_ACTIVE',
        content: t('settings.notifications.routing.mode.lastActive'),
      },
      {
        value: 'RING_ALL',
        content: t('settings.notifications.routing.mode.ringAll'),
      },
      {
        value: 'PRIMARY_ONLY',
        content: t('settings.notifications.routing.mode.primaryOnly'),
      },
    ],
    [t]
  );

  const eventRoutingOptions = useMemo(
    () => [
      {
        value: 'inherit',
        content: t(
          'settings.notifications.eventRules.deviceRouting.mode.inherit'
        ),
      },
      ...routingOptions,
    ],
    [routingOptions, t]
  );

  const timezoneOptions = useMemo<SearchableSelectOption[]>(() => {
    const supportedValuesOf = (
      Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf;
    const supported = supportedValuesOf ? supportedValuesOf('timeZone') : [];
    const zones = supported.length > 0 ? supported : ['UTC'];

    return zones.map((zone) => ({
      value: zone,
      content: zone,
      keywords: zone,
    }));
  }, []);

  const timezoneSelectOptions = useMemo(() => {
    const current = localPreferences?.dnd?.timezone;
    if (!current) {
      return timezoneOptions;
    }

    const exists = timezoneOptions.some((option) => option.value === current);
    if (exists) {
      return timezoneOptions;
    }

    return [
      {
        value: current,
        content: current,
        keywords: current,
      },
      ...timezoneOptions,
    ];
  }, [localPreferences?.dnd?.timezone, timezoneOptions]);

  const dndDayOptions = useMemo(
    () => [
      { value: 'MON', content: t('settings.notifications.dnd.days.mon') },
      { value: 'TUE', content: t('settings.notifications.dnd.days.tue') },
      { value: 'WED', content: t('settings.notifications.dnd.days.wed') },
      { value: 'THU', content: t('settings.notifications.dnd.days.thu') },
      { value: 'FRI', content: t('settings.notifications.dnd.days.fri') },
      { value: 'SAT', content: t('settings.notifications.dnd.days.sat') },
      { value: 'SUN', content: t('settings.notifications.dnd.days.sun') },
    ],
    [t]
  );

  const eventAlertOptions = useMemo(
    () => [
      {
        value: 'inherit',
        content: t('settings.notifications.eventRules.alert.inherit'),
      },
      {
        value: 'alert',
        content: t('settings.notifications.eventRules.alert.alert'),
      },
      {
        value: 'badge',
        content: t('settings.notifications.eventRules.alert.badge'),
      },
    ],
    [t]
  );

  const eventSoundOptions = useMemo(
    () => [
      {
        value: 'inherit',
        content: t('settings.notifications.eventRules.sound.inherit'),
      },
      {
        value: 'on',
        content: t('settings.notifications.eventRules.sound.on'),
      },
      {
        value: 'off',
        content: t('settings.notifications.eventRules.sound.off'),
      },
    ],
    [t]
  );

  const deliveryChannelLabels = useMemo(
    () => ({
      IN_APP: t('settings.notifications.deliveryChannels.inApp'),
      PUSH: t('settings.notifications.deliveryChannels.push'),
      EMAIL: t('settings.notifications.deliveryChannels.email'),
      SMS: t('settings.notifications.deliveryChannels.sms'),
      WHATSAPP: t('settings.notifications.deliveryChannels.whatsapp'),
    }),
    [t]
  );

  const hasChanges = useMemo(() => {
    if (!baselinePreferences || !localPreferences) {
      return false;
    }

    return (
      JSON.stringify(stripPreferences(baselinePreferences)) !==
      JSON.stringify(stripPreferences(localPreferences))
    );
  }, [baselinePreferences, localPreferences]);

  const updatePreferences = useCallback(
    (
      updater: (current: NotificationPreferences) => NotificationPreferences
    ) => {
      setLocalPreferences((current) => (current ? updater(current) : current));
    },
    []
  );

  const updateDnd = useCallback(
    (updater: (current: NotificationDnd) => NotificationDnd) => {
      updatePreferences((current) => {
        const nextDnd = updater(current.dnd ?? { ...DEFAULT_DND });
        const normalizedSchedules = (nextDnd.schedules ?? []).map(
          (schedule) => ({
            ...schedule,
            enabled: true,
            daysOfWeek: schedule.daysOfWeek,
            channels: [...ALL_DND_CHANNELS],
            behavior: undefined,
            priority: undefined,
          })
        );

        return {
          ...current,
          dnd: {
            ...nextDnd,
            snoozeUntil: undefined,
            behavior: {
              createInboxItem: true,
            },
            schedules: normalizedSchedules,
          },
        };
      });
    },
    [updatePreferences]
  );

  const eventDefinitions = useMemo(
    () => eventsResponse?.items ?? [],
    [eventsResponse]
  );

  const appEventDefinitions = useMemo(
    () =>
      eventDefinitions.filter((definition) => definition.category === 'APP'),
    [eventDefinitions]
  );

  const marketingEventDefinitions = useMemo(
    () =>
      eventDefinitions.filter(
        (definition) => definition.category === 'MARKETING'
      ),
    [eventDefinitions]
  );

  const endpointOptions = useMemo(() => {
    const endpoints = endpointsResponse ?? [];
    const sorted = [...endpoints].sort((a, b) => {
      const left = a.deviceName?.trim() ? a.deviceName.trim() : a.deviceId;
      const right = b.deviceName?.trim() ? b.deviceName.trim() : b.deviceId;
      return left.localeCompare(right);
    });

    return [
      {
        value: '',
        content: t('settings.notifications.routing.primaryEndpoint.none'),
      },
      ...sorted.map((endpoint) => {
        const deviceLabel = endpoint.deviceName?.trim()
          ? endpoint.deviceName.trim()
          : endpoint.deviceId;
        const platformLabel = endpoint.platform.replace(/_/g, ' ');
        return {
          value: endpoint.id,
          content: `${deviceLabel} (${platformLabel})`,
        };
      }),
    ];
  }, [endpointsResponse, t]);

  const endpointsStatusMessage = useMemo(() => {
    if (endpointsError) {
      return t('settings.notifications.routing.primaryEndpoint.error');
    }

    if (!isEndpointsLoading && (endpointsResponse?.length ?? 0) === 0) {
      return t('settings.notifications.routing.primaryEndpoint.empty');
    }

    return null;
  }, [endpointsError, endpointsResponse, isEndpointsLoading, t]);

  const updateEventRule = useCallback(
    (
      eventKey: NotificationEventKey,
      updater: (rule: NotificationEventRule) => NotificationEventRule
    ) => {
      updatePreferences((current) => {
        const existingRules = normalizeEventRules(current.eventRules);
        const currentRule =
          existingRules.find((rule) => rule.eventKey === eventKey) ??
          ({ eventKey } as NotificationEventRule);

        const nextRule = updater(currentRule);
        const nextRules = existingRules.filter(
          (rule) => rule.eventKey !== eventKey
        );

        const hasInAppOverrides =
          nextRule.inApp?.showAlert !== undefined ||
          nextRule.inApp?.playSound !== undefined;
        const hasRoutingOverrides =
          nextRule.routing?.mode !== undefined ||
          nextRule.routing?.primaryEndpointId !== undefined;
        const hasDeliveryOverrides =
          (nextRule.deliveryChannels?.length ?? 0) > 0;
        const hasOverrides =
          nextRule.enabled !== undefined ||
          hasInAppOverrides ||
          hasRoutingOverrides ||
          hasDeliveryOverrides;

        if (hasOverrides) {
          nextRules.push({
            ...nextRule,
            eventKey,
          });
        }

        return {
          ...current,
          eventRules: normalizeEventRules(nextRules),
        };
      });
    },
    [updatePreferences]
  );

  const renderEventDefinition = useCallback(
    (definition: NotificationEventDefinition) => {
      if (!localPreferences) {
        return null;
      }

      const rule = localPreferences.eventRules?.find(
        (eventRule) => eventRule.eventKey === definition.eventKey
      );
      const defaultEnabled =
        definition.defaultEnabled ??
        isChannelEnabled(localPreferences.channels, definition.channel);
      const enabled = rule?.enabled ?? defaultEnabled;

      const resolvedLabel = definition.labelKey
        ? t(definition.labelKey as TranslationKey)
        : definition.label;
      const label =
        definition.labelKey && resolvedLabel === definition.labelKey
          ? definition.label
          : resolvedLabel;

      const resolvedDescription = definition.descriptionKey
        ? t(definition.descriptionKey as TranslationKey)
        : definition.description;
      const description =
        definition.descriptionKey &&
        resolvedDescription === definition.descriptionKey
          ? definition.description
          : resolvedDescription;

      const alertValue =
        rule?.inApp?.showAlert === undefined
          ? 'inherit'
          : rule.inApp.showAlert
            ? 'alert'
            : 'badge';
      const soundValue =
        rule?.inApp?.playSound === undefined
          ? 'inherit'
          : rule.inApp.playSound
            ? 'on'
            : 'off';

      const restrictedChannels = restrictPhoneChannels
        ? PHONE_DELIVERY_CHANNELS
        : [];
      const { resolvedAvailable, effectiveDefault, deliveryChannelsValue } =
        resolveNotificationDeliverySelection({
          available: definition.availableDeliveryChannels,
          defaults: definition.defaultDeliveryChannels,
          selected: rule?.deliveryChannels,
          all: ALL_DELIVERY_CHANNELS,
          fallback: DEFAULT_DELIVERY_CHANNELS,
          restricted: restrictedChannels,
        });
      const deliveryChannelOptions = resolvedAvailable.map((channel) => ({
        value: channel,
        content: deliveryChannelLabels[channel],
        disabled:
          restrictedChannels.length > 0 && restrictedChannels.includes(channel),
      }));

      return (
        <div
          key={definition.eventKey}
          className="rounded-lg border border-secondary-200 bg-secondary-50 p-4"
        >
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <Text weight="medium" className="text-sm text-secondary-800">
                {label}
              </Text>
              {description && (
                <Text size="small" color="neutral" className="mt-1">
                  {description}
                </Text>
              )}
            </div>
            <LabelSwitch
              label={t('settings.notifications.eventRules.enabled')}
              checked={enabled}
              onChange={(checked) =>
                updateEventRule(definition.eventKey, (currentRule) => ({
                  ...currentRule,
                  enabled: checked === defaultEnabled ? undefined : checked,
                }))
              }
              labelPosition="top"
              disabled={updateMutation.isPending}
            />
          </div>

          <div className="mt-4 space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <LabelSelect
                label={t('settings.notifications.eventRules.alert.label')}
                options={eventAlertOptions}
                labelPosition="top"
                selectProps={{
                  value: alertValue,
                  onChange: (e) =>
                    updateEventRule(definition.eventKey, (currentRule) => ({
                      ...currentRule,
                      inApp: {
                        ...currentRule.inApp,
                        showAlert:
                          e.target.value === 'inherit'
                            ? undefined
                            : e.target.value === 'alert',
                      },
                    })),
                }}
              />
              <LabelSelect
                label={t('settings.notifications.eventRules.sound.label')}
                options={eventSoundOptions}
                labelPosition="top"
                selectProps={{
                  value: soundValue,
                  onChange: (e) =>
                    updateEventRule(definition.eventKey, (currentRule) => ({
                      ...currentRule,
                      inApp: {
                        ...currentRule.inApp,
                        playSound:
                          e.target.value === 'inherit'
                            ? undefined
                            : e.target.value === 'on',
                      },
                    })),
                }}
              />
            </div>
            <div className="space-y-1">
              <Label>
                {t('settings.notifications.eventRules.deliveryChannels.label')}
              </Label>
              <MultiSelect
                options={deliveryChannelOptions}
                value={deliveryChannelsValue}
                disabled={updateMutation.isPending}
                onChange={(values) => {
                  const normalized = normalizeDeliverySelection(
                    values,
                    resolvedAvailable
                  ).filter((channel) => !restrictedChannels.includes(channel));
                  const nextSelection =
                    normalized.length > 0 ? normalized : effectiveDefault;
                  const shouldOverride = !areDeliverySelectionsEqual(
                    nextSelection,
                    effectiveDefault
                  );
                  updateEventRule(definition.eventKey, (currentRule) => ({
                    ...currentRule,
                    deliveryChannels: shouldOverride
                      ? nextSelection
                      : undefined,
                  }));
                }}
              />
            </div>
          </div>
        </div>
      );
    },
    [
      deliveryChannelLabels,
      eventAlertOptions,
      eventSoundOptions,
      localPreferences,
      restrictPhoneChannels,
      t,
      updateEventRule,
      updateMutation.isPending,
    ]
  );

  const renderEventRoutingDefinition = useCallback(
    (definition: NotificationEventDefinition) => {
      if (!localPreferences) {
        return null;
      }

      const rule = localPreferences.eventRules?.find(
        (eventRule) => eventRule.eventKey === definition.eventKey
      );

      const resolvedLabel = definition.labelKey
        ? t(definition.labelKey as TranslationKey)
        : definition.label;
      const label =
        definition.labelKey && resolvedLabel === definition.labelKey
          ? definition.label
          : resolvedLabel;

      const resolvedDescription = definition.descriptionKey
        ? t(definition.descriptionKey as TranslationKey)
        : definition.description;
      const description =
        definition.descriptionKey &&
        resolvedDescription === definition.descriptionKey
          ? definition.description
          : resolvedDescription;

      const routingModeValue = rule?.routing?.mode ?? 'inherit';

      return (
        <div
          key={definition.eventKey}
          className="rounded-lg border border-secondary-200 bg-secondary-50 p-4"
        >
          <div className="flex flex-col gap-2">
            <Text weight="medium" className="text-sm text-secondary-800">
              {label}
            </Text>
            {description && (
              <Text size="small" color="neutral">
                {description}
              </Text>
            )}
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <LabelSelect
              label={t(
                'settings.notifications.eventRules.deviceRouting.mode.label'
              )}
              options={eventRoutingOptions}
              labelPosition="top"
              selectProps={{
                value: routingModeValue,
                onChange: (e) =>
                  updateEventRule(definition.eventKey, (currentRule) => {
                    const nextMode = e.target.value;
                    if (nextMode === 'inherit') {
                      return {
                        ...currentRule,
                        routing: undefined,
                      };
                    }

                    return {
                      ...currentRule,
                      routing: {
                        ...currentRule.routing,
                        mode: nextMode as NotificationRoutingMode,
                        primaryEndpointId:
                          nextMode === 'PRIMARY_ONLY'
                            ? currentRule.routing?.primaryEndpointId
                            : undefined,
                      },
                    };
                  }),
                disabled: updateMutation.isPending,
              }}
            />
            {routingModeValue === 'PRIMARY_ONLY' && (
              <LabelSelect
                label={t('settings.notifications.routing.primaryEndpoint')}
                options={endpointOptions}
                labelPosition="top"
                selectProps={{
                  value: rule?.routing?.primaryEndpointId ?? '',
                  onChange: (e) =>
                    updateEventRule(definition.eventKey, (currentRule) => ({
                      ...currentRule,
                      routing: {
                        ...currentRule.routing,
                        mode: 'PRIMARY_ONLY',
                        primaryEndpointId: e.target.value || undefined,
                      },
                    })),
                  loading: isEndpointsLoading,
                  disabled: isEndpointsLoading || updateMutation.isPending,
                }}
              />
            )}
          </div>
          {routingModeValue === 'PRIMARY_ONLY' && endpointsStatusMessage && (
            <Text size="small" color="neutral" className="mt-2">
              {endpointsStatusMessage}
            </Text>
          )}
          {routingModeValue === 'PRIMARY_ONLY' && (
            <Text size="small" color="neutral" className="mt-2">
              {t(
                'settings.notifications.eventRules.deviceRouting.primaryEndpoint.helper'
              )}
            </Text>
          )}
        </div>
      );
    },
    [
      endpointOptions,
      endpointsStatusMessage,
      eventRoutingOptions,
      isEndpointsLoading,
      localPreferences,
      t,
      updateEventRule,
      updateMutation.isPending,
    ]
  );

  const renderEventRoutingContent = useCallback(
    (definitions: NotificationEventDefinition[]) => {
      if (isEventsLoading) {
        return (
          <div className="flex items-center gap-2">
            <Loading size="sm" />
          </div>
        );
      }

      if (eventsError) {
        return (
          <Text size="small" color="neutral">
            {t('settings.notifications.eventRules.error')}
          </Text>
        );
      }

      if (definitions.length === 0) {
        return (
          <Text size="small" color="neutral">
            {t('settings.notifications.eventRules.empty')}
          </Text>
        );
      }

      return (
        <div className="space-y-3">
          {definitions.map(renderEventRoutingDefinition)}
        </div>
      );
    },
    [eventsError, isEventsLoading, renderEventRoutingDefinition, t]
  );

  const renderEventRulesContent = useCallback(
    (definitions: NotificationEventDefinition[]) => {
      if (isEventsLoading) {
        return (
          <div className="flex items-center gap-2">
            <Loading size="sm" />
          </div>
        );
      }

      if (eventsError) {
        return (
          <Text size="small" color="neutral">
            {t('settings.notifications.eventRules.error')}
          </Text>
        );
      }

      if (definitions.length === 0) {
        return (
          <Text size="small" color="neutral">
            {t('settings.notifications.eventRules.empty')}
          </Text>
        );
      }

      const showPhoneWarning =
        restrictPhoneChannels &&
        definitions.some((definition) => {
          const availableChannels = definition.availableDeliveryChannels?.length
            ? definition.availableDeliveryChannels
            : definition.defaultDeliveryChannels?.length
              ? definition.defaultDeliveryChannels
              : DEFAULT_DELIVERY_CHANNELS;
          return availableChannels.some((channel) =>
            PHONE_DELIVERY_CHANNELS.includes(channel)
          );
        });

      return (
        <div className="space-y-3">
          {showPhoneWarning && (
            <Alert
              variant="warning"
              appearance="soft"
              message={t(
                'settings.notifications.eventRules.deliveryChannels.phoneRequired'
              )}
            />
          )}
          {definitions.map(renderEventDefinition)}
        </div>
      );
    },
    [
      eventsError,
      isEventsLoading,
      renderEventDefinition,
      restrictPhoneChannels,
      t,
    ]
  );

  const handleSave = useCallback(() => {
    if (!localPreferences) {
      return;
    }

    if (localPreferences.dnd?.enabled && !localPreferences.dnd.timezone) {
      showAlert(t('settings.notifications.alerts.missingTimezone'), 'error');
      return;
    }

    const payload: NotificationPreferencesUpdateBodyParams = {
      channels: localPreferences.channels,
      routing: localPreferences.routing,
      inApp: localPreferences.inApp,
      eventRules: localPreferences.eventRules,
      dnd: localPreferences.dnd,
    };

    updateMutation.mutate(payload, {
      onSuccess: (data) => {
        const nextPreferences = buildPreferences(data);
        setLocalPreferences(nextPreferences);
        showAlert(t('settings.notifications.alerts.updated'), 'success');
      },
      onError: () => {
        showAlert(t('settings.notifications.alerts.error'), 'error');
      },
    });
  }, [localPreferences, showAlert, t, updateMutation]);

  const handleReset = useCallback(() => {
    if (baselinePreferences) {
      setLocalPreferences(baselinePreferences);
    }
  }, [baselinePreferences]);

  const pageTitle = useMemo(() => t('settings.notifications.title'), [t]);

  const headerActions = useMemo(() => {
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          appearance="outline"
          size="md"
          onClick={handleReset}
          disabled={!hasChanges || updateMutation.isPending}
        >
          {t('settings.notifications.actions.reset')}
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={handleSave}
          disabled={!hasChanges || updateMutation.isPending}
          loading={updateMutation.isPending}
        >
          {t('settings.notifications.actions.save')}
        </Button>
      </div>
    );
  }, [handleReset, handleSave, hasChanges, t, updateMutation.isPending]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loading size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState
        title={t('settings.notifications.error.title')}
        description={t('settings.notifications.error.description')}
        actionContent={t('settings.notifications.error.retry')}
        onAction={refetch}
      />
    );
  }

  if (!localPreferences) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <Panel className="h-full flex flex-col">
        <PanelHeader className="px-4 py-3 border-b" actions={headerActions}>
          <div className="flex items-center gap-4">
            <PiBellLight className="w-8 h-8" />
            <div>
              <Text as="h2" size="h2" weight="medium">
                {pageTitle}
              </Text>
              <Text size="small" color="neutral" className="mt-1">
                {t('settings.notifications.description')}
              </Text>
            </div>
          </div>
        </PanelHeader>

        <div className="flex-1 overflow-auto p-4">
          <div className="space-y-6">
            <SectionCard
              title={t('settings.notifications.sections.app.title')}
              description={t('settings.notifications.sections.app.description')}
            >
              <div className="space-y-4">
                <LabelSwitch
                  label={t('settings.notifications.sections.app.toggle')}
                  checked={
                    localPreferences.channels.messaging ||
                    localPreferences.channels.calls
                  }
                  onChange={(checked) =>
                    updatePreferences((current) => ({
                      ...current,
                      channels: {
                        ...current.channels,
                        messaging: checked,
                        calls: checked,
                      },
                    }))
                  }
                  disabled={updateMutation.isPending}
                  labelPosition="top"
                />
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-4">
                    <Text
                      weight="medium"
                      className="text-sm text-secondary-800"
                    >
                      {t('settings.notifications.inApp.alert')}
                    </Text>
                    <Text size="small" color="neutral" className="mt-1">
                      {t('settings.notifications.inApp.alert.helper')}
                    </Text>
                    <div className="mt-4">
                      <Tabs
                        value={
                          localPreferences.inApp.showAlert ? 'alert' : 'badge'
                        }
                        onChange={(value) =>
                          updatePreferences((current) => ({
                            ...current,
                            inApp: {
                              ...current.inApp,
                              showAlert: value === 'alert',
                            },
                          }))
                        }
                        size="sm"
                        appearance="outline"
                        align="center"
                      >
                        <Tab
                          value="alert"
                          label={t(
                            'settings.notifications.inApp.alert.option.alert'
                          )}
                        />
                        <Tab
                          value="badge"
                          label={t(
                            'settings.notifications.inApp.alert.option.badge'
                          )}
                        />
                      </Tabs>
                    </div>
                  </div>
                  <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-4">
                    <Text
                      weight="medium"
                      className="text-sm text-secondary-800"
                    >
                      {t('settings.notifications.inApp.sound')}
                    </Text>
                    <Text size="small" color="neutral" className="mt-1">
                      {t('settings.notifications.inApp.sound.helper')}
                    </Text>
                    <div className="mt-4">
                      <Tabs
                        value={localPreferences.inApp.playSound ? 'on' : 'off'}
                        onChange={(value) =>
                          updatePreferences((current) => ({
                            ...current,
                            inApp: {
                              ...current.inApp,
                              playSound: value === 'on',
                            },
                          }))
                        }
                        size="sm"
                        appearance="outline"
                        align="center"
                      >
                        <Tab
                          value="on"
                          label={t(
                            'settings.notifications.inApp.sound.option.on'
                          )}
                        />
                        <Tab
                          value="off"
                          label={t(
                            'settings.notifications.inApp.sound.option.off'
                          )}
                        />
                      </Tabs>
                    </div>
                  </div>
                </div>
              </div>
              <ExpandableSection
                title={t('settings.notifications.sections.app.advanced.toggle')}
                expanded={showAppAdvanced}
                onExpandChange={setShowAppAdvanced}
                expansionControl="left-icon"
                isContentScrollable={false}
                showScrollHint={false}
                contentClassName="max-h-none"
                titleClassName="text-sm"
              >
                <div className="space-y-6 border-t border-secondary-200 pt-4">
                  <div className="space-y-3">
                    <div>
                      <Text
                        weight="medium"
                        className="text-sm text-secondary-800"
                      >
                        {t('settings.notifications.eventRules.title')}
                      </Text>
                      <Text size="small" color="neutral" className="mt-1">
                        {t('settings.notifications.eventRules.description')}
                      </Text>
                    </div>
                    {renderEventRulesContent(appEventDefinitions)}
                  </div>
                </div>
              </ExpandableSection>
            </SectionCard>

            <SectionCard
              title={t('settings.notifications.sections.marketing.title')}
              description={t(
                'settings.notifications.sections.marketing.description'
              )}
            >
              <div className="space-y-4">
                <LabelSwitch
                  label={t('settings.notifications.sections.marketing.toggle')}
                  checked={localPreferences.channels.marketing}
                  onChange={(checked) =>
                    updatePreferences((current) => ({
                      ...current,
                      channels: {
                        ...current.channels,
                        marketing: checked,
                      },
                    }))
                  }
                  disabled={updateMutation.isPending}
                  labelPosition="top"
                />
              </div>

              <ExpandableSection
                title={t(
                  'settings.notifications.sections.marketing.advanced.toggle'
                )}
                expanded={showMarketingAdvanced}
                onExpandChange={setShowMarketingAdvanced}
                expansionControl="left-icon"
                isContentScrollable={false}
                showScrollHint={false}
                contentClassName="max-h-none"
                titleClassName="text-sm"
              >
                <div className="space-y-3 border-t border-secondary-200 pt-4">
                  <div>
                    <Text
                      weight="medium"
                      className="text-sm text-secondary-800"
                    >
                      {t('settings.notifications.eventRules.title')}
                    </Text>
                    <Text size="small" color="neutral" className="mt-1">
                      {t('settings.notifications.eventRules.description')}
                    </Text>
                  </div>
                  {renderEventRulesContent(marketingEventDefinitions)}
                </div>
              </ExpandableSection>
            </SectionCard>

            <SectionCard
              title={t('settings.notifications.sections.routing.title')}
              description={t(
                'settings.notifications.sections.routing.description'
              )}
            >
              <div className="space-y-3">
                <LabelSelect
                  label={t('settings.notifications.routing.mode.label')}
                  options={routingOptions}
                  labelPosition="top"
                  selectProps={{
                    value: localPreferences.routing.mode,
                    onChange: (e) =>
                      updatePreferences((current) => ({
                        ...current,
                        routing: {
                          ...current.routing,
                          mode: e.target.value as NotificationRoutingMode,
                        },
                      })),
                  }}
                />
                {localPreferences.routing.mode === 'PRIMARY_ONLY' && (
                  <div className="mt-4 space-y-2">
                    <LabelSelect
                      label={t(
                        'settings.notifications.routing.primaryEndpoint'
                      )}
                      options={endpointOptions}
                      labelPosition="top"
                      selectProps={{
                        value: localPreferences.routing.primaryEndpointId ?? '',
                        onChange: (e) =>
                          updatePreferences((current) => ({
                            ...current,
                            routing: {
                              ...current.routing,
                              primaryEndpointId: e.target.value || undefined,
                            },
                          })),
                        loading: isEndpointsLoading,
                        disabled: isEndpointsLoading,
                      }}
                    />
                    <Text size="small" color="neutral">
                      {t(
                        'settings.notifications.routing.primaryEndpoint.helper'
                      )}
                    </Text>
                    {endpointsStatusMessage && (
                      <Text size="small" color="neutral">
                        {endpointsStatusMessage}
                      </Text>
                    )}
                  </div>
                )}
              </div>

              <ExpandableSection
                title={t('settings.notifications.routing.advanced.toggle')}
                expanded={showRoutingAdvanced}
                onExpandChange={setShowRoutingAdvanced}
                expansionControl="left-icon"
                isContentScrollable={false}
                showScrollHint={false}
                contentClassName="max-h-none"
                titleClassName="text-sm"
              >
                <div className="space-y-6 border-t border-secondary-200 pt-4">
                  <div>
                    <Text
                      weight="medium"
                      className="text-sm text-secondary-800"
                    >
                      {t('settings.notifications.routing.advanced.title')}
                    </Text>
                    <Text size="small" color="neutral" className="mt-1">
                      {t('settings.notifications.routing.advanced.description')}
                    </Text>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-3">
                      <Text
                        weight="medium"
                        className="text-sm text-secondary-800"
                      >
                        {t('settings.notifications.eventRules.group.app')}
                      </Text>
                      {renderEventRoutingContent(appEventDefinitions)}
                    </div>
                    <div className="space-y-3">
                      <Text
                        weight="medium"
                        className="text-sm text-secondary-800"
                      >
                        {t('settings.notifications.eventRules.group.marketing')}
                      </Text>
                      {renderEventRoutingContent(marketingEventDefinitions)}
                    </div>
                  </div>
                </div>
              </ExpandableSection>
            </SectionCard>

            <SectionCard
              title={t('settings.notifications.sections.dnd.title')}
              description={t('settings.notifications.sections.dnd.description')}
            >
              <div className="space-y-4">
                <LabelSwitch
                  label={t('settings.notifications.dnd.enabled')}
                  checked={localPreferences.dnd?.enabled ?? false}
                  onChange={(checked) =>
                    updateDnd((current) => ({
                      ...current,
                      enabled: checked,
                    }))
                  }
                  disabled={updateMutation.isPending}
                  labelPosition="top"
                />

                {localPreferences.dnd?.enabled && (
                  <div className="space-y-6">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-1">
                        <Label>
                          {t('settings.notifications.dnd.timezone')}
                        </Label>
                        <SearchableSelect
                          options={timezoneSelectOptions}
                          value={localPreferences.dnd.timezone ?? ''}
                          onChange={(value) =>
                            updateDnd((current) => ({
                              ...current,
                              timezone: value === '' ? undefined : value,
                            }))
                          }
                          placeholder={t(
                            'settings.notifications.dnd.timezone.placeholder'
                          )}
                          disabled={updateMutation.isPending}
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Text
                          weight="medium"
                          className="text-sm text-secondary-800"
                        >
                          {t('settings.notifications.dnd.schedules.title')}
                        </Text>
                        <Button
                          variant="secondary"
                          appearance="outline"
                          size="sm"
                          className="flex items-center gap-2"
                          onClick={() =>
                            updateDnd((current) => ({
                              ...current,
                              schedules: [
                                ...(current.schedules ?? []),
                                createSchedule(),
                              ],
                            }))
                          }
                        >
                          <PiPlus className="w-4 h-4" />
                          {t('settings.notifications.dnd.schedules.add')}
                        </Button>
                      </div>

                      {(localPreferences.dnd.schedules ?? []).length === 0 && (
                        <Text size="small" color="neutral">
                          {t('settings.notifications.dnd.schedules.empty')}
                        </Text>
                      )}

                      {(localPreferences.dnd.schedules ?? []).map(
                        (schedule, index) => {
                          const selectedDays = schedule.daysOfWeek;
                          const scheduleLabel = schedule.name?.trim()
                            ? schedule.name.trim()
                            : t('settings.notifications.dnd.schedules.item', {
                                index: index + 1,
                              });

                          return (
                            <div
                              key={schedule.id}
                              className="rounded-lg border border-secondary-200 bg-secondary-50 p-4"
                            >
                              <div className="flex items-center justify-between">
                                <Text weight="medium" className="text-sm">
                                  {scheduleLabel}
                                </Text>
                                <Button
                                  variant="danger"
                                  appearance="ghost"
                                  size="xs"
                                  isIconOnly
                                  onClick={() =>
                                    updateDnd((current) => ({
                                      ...current,
                                      schedules: (
                                        current.schedules ?? []
                                      ).filter(
                                        (item) => item.id !== schedule.id
                                      ),
                                    }))
                                  }
                                >
                                  <PiTrash className="w-4 h-4" />
                                </Button>
                              </div>

                              <div className="mt-3 space-y-4">
                                <div className="rounded-lg border border-secondary-200 bg-white p-3">
                                  <Text
                                    size="small"
                                    className="text-secondary-700"
                                  >
                                    {t(
                                      'settings.notifications.dnd.schedules.range'
                                    )}
                                  </Text>
                                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                                    <LabelTextInput
                                      label={t(
                                        'settings.notifications.dnd.schedules.start'
                                      )}
                                      labelPosition="top"
                                      inputProps={{
                                        type: 'time',
                                        value: schedule.start,
                                        onChange: (e) =>
                                          updateDnd((current) => ({
                                            ...current,
                                            schedules: (
                                              current.schedules ?? []
                                            ).map((item) =>
                                              item.id === schedule.id
                                                ? {
                                                    ...item,
                                                    start: e.target.value,
                                                  }
                                                : item
                                            ),
                                          })),
                                      }}
                                    />
                                    <LabelTextInput
                                      label={t(
                                        'settings.notifications.dnd.schedules.end'
                                      )}
                                      labelPosition="top"
                                      inputProps={{
                                        type: 'time',
                                        value: schedule.end,
                                        onChange: (e) =>
                                          updateDnd((current) => ({
                                            ...current,
                                            schedules: (
                                              current.schedules ?? []
                                            ).map((item) =>
                                              item.id === schedule.id
                                                ? {
                                                    ...item,
                                                    end: e.target.value,
                                                  }
                                                : item
                                            ),
                                          })),
                                      }}
                                    />
                                  </div>
                                </div>

                                <div className="rounded-lg border border-secondary-200 bg-white p-3">
                                  <Text
                                    size="small"
                                    className="text-secondary-700"
                                  >
                                    {t(
                                      'settings.notifications.dnd.schedules.dateRange.title'
                                    )}
                                  </Text>
                                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                                    <LabelTextInput
                                      label={t(
                                        'settings.notifications.dnd.schedules.dateRange.start'
                                      )}
                                      labelPosition="top"
                                      inputProps={{
                                        type: 'date',
                                        value: schedule.startDate ?? '',
                                        onChange: (e) =>
                                          updateDnd((current) => ({
                                            ...current,
                                            schedules: (
                                              current.schedules ?? []
                                            ).map((item) =>
                                              item.id === schedule.id
                                                ? {
                                                    ...item,
                                                    startDate: e.target.value,
                                                  }
                                                : item
                                            ),
                                          })),
                                      }}
                                    />
                                    <LabelTextInput
                                      label={t(
                                        'settings.notifications.dnd.schedules.dateRange.end'
                                      )}
                                      labelPosition="top"
                                      inputProps={{
                                        type: 'date',
                                        value: schedule.endDate ?? '',
                                        onChange: (e) =>
                                          updateDnd((current) => ({
                                            ...current,
                                            schedules: (
                                              current.schedules ?? []
                                            ).map((item) =>
                                              item.id === schedule.id
                                                ? {
                                                    ...item,
                                                    endDate: e.target.value,
                                                  }
                                                : item
                                            ),
                                          })),
                                      }}
                                    />
                                  </div>
                                </div>

                                <div>
                                  <Text
                                    size="small"
                                    className="text-secondary-700"
                                  >
                                    {t(
                                      'settings.notifications.dnd.schedules.days'
                                    )}
                                  </Text>
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    {dndDayOptions.map((option) => {
                                      const day =
                                        option.value as NotificationDndDay;
                                      const isActive =
                                        selectedDays.includes(day);

                                      return (
                                        <button
                                          key={option.value}
                                          type="button"
                                          className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                                            isActive
                                              ? 'border-secondary-900 bg-secondary-900 text-white'
                                              : 'border-secondary-200 bg-white text-secondary-700'
                                          }`}
                                          onClick={() => {
                                            const nextDays = isActive
                                              ? selectedDays.filter(
                                                  (value) => value !== day
                                                )
                                              : [...selectedDays, day];
                                            const normalizedDays =
                                              nextDays.length > 0
                                                ? nextDays
                                                : selectedDays;

                                            updateDnd((current) => ({
                                              ...current,
                                              schedules: (
                                                current.schedules ?? []
                                              ).map((item) =>
                                                item.id === schedule.id
                                                  ? {
                                                      ...item,
                                                      daysOfWeek:
                                                        normalizedDays,
                                                    }
                                                  : item
                                              ),
                                            }));
                                          }}
                                        >
                                          {option.content}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
              </div>
            </SectionCard>
          </div>
        </div>
      </Panel>
    </div>
  );
};
